import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, message, Button, Space, Tag } from "antd";
import type { TableProps } from "antd";
import { useGlobal } from "../contexts/GlobalContext";
import request from "../utils/request";
import { normalizeV3Order, type V3Order } from "../utils/v3Compat";
import "remixicon/fonts/remixicon.css";

interface OrderItem {
  id: number;
  order_id: string;
  order_amount: number;
  pay_amount: number;
  pay_type: string;
  pay_status: number;
  product_name: string;
  product_count: number;
  created_at: string;
  pay_time: string;
  pay_url?: string;
}

interface OrderResponse {
  total: number;
  list: V3Order[];
}

const MyOrders: React.FC = () => {
  const { token, API_BASE_URL } = useGlobal();
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<Record<string, boolean>>(
    {}
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const paginationRef = React.useRef(pagination);

  // 更新 ref 当 pagination 改变时
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const fetchOrders = useCallback(
    async (params?: { current?: number; pageSize?: number }) => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await request.get<{
          code: number;
          msg: string;
          data: OrderResponse;
        }>(`${API_BASE_URL}/api/v3/orders`, {
          params: {
            page: params?.current || paginationRef.current.current,
            page_size: params?.pageSize || paginationRef.current.pageSize,
          },
        });

        if (response.data.code === 200) {
          setOrders((response.data.data.list || []).map(normalizeV3Order));
          setPagination((prev) => ({
            ...prev,
            current: params?.current || prev.current,
            pageSize: params?.pageSize || prev.pageSize,
            total: response.data.data.total,
          }));
        } else {
          message.error(response.data.msg || "获取订单列表失败");
        }
      } catch (error) {
        console.error("获取订单列表失败", error);
        message.error("获取订单列表失败，请稍后重试");
      } finally {
        setLoading(false);
      }
    },
    [token, API_BASE_URL]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleTableChange = (paginationParams: any) => {
    fetchOrders({
      current: paginationParams.current,
      pageSize: paginationParams.pageSize,
    });
  };

  // 刷新订单列表
  const refreshOrders = () => {
    fetchOrders();
  };

  // 跳转到支付链接
  const handleGoToPay = (payUrl: string) => {
    if (payUrl) {
      window.open(payUrl, "_blank");
    } else {
      message.error("支付链接不可用");
    }
  };

  // 检查支付状态
  const checkPayStatus = async (orderId: string) => {
    if (!token) return;

    setCheckingStatus((prev) => ({ ...prev, [orderId]: true }));
    try {
      const response = await request.get<{
        code: number;
        msg: string;
        data: V3Order;
      }>(`${API_BASE_URL}/api/v3/orders/${orderId}`);

      if (response.data.code === 200) {
        const nextOrder = normalizeV3Order(response.data.data);
        const payStatus = nextOrder.pay_status === 1;

        // 更新当前订单的状态
        setOrders((prev) =>
          prev.map((order) =>
            order.order_id === orderId
              ? { ...order, ...nextOrder }
              : order
          )
        );

        if (payStatus) {
          message.success("订单已支付成功");
        } else {
          message.info("订单未支付");
        }
      } else {
        message.error(response.data.msg || "检查订单状态失败");
      }
    } catch (error) {
      console.error("检查订单状态失败", error);
      message.error("检查订单状态失败，请稍后重试");
    } finally {
      setCheckingStatus((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const columns: TableProps<OrderItem>["columns"] = [
    {
      title: "订单号",
      dataIndex: "order_id",
      key: "order_id",
    },
    {
      title: "商品",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "数量",
      dataIndex: "product_count",
      key: "product_count",
    },
    {
      title: "订单金额",
      dataIndex: "order_amount",
      key: "order_amount",
      render: (amount: number) => `¥${(amount / 100).toFixed(2)}`,
    },
    {
      title: "实付金额",
      dataIndex: "pay_amount",
      key: "pay_amount",
      render: (amount: number) => `¥${(amount / 100).toFixed(2)}`,
    },
    {
      title: "支付方式",
      dataIndex: "pay_type",
      key: "pay_type",
      render: (payType: string) => {
        const payTypeMap: Record<string, { text: string; color: string }> = {
          alipay: { text: "支付宝", color: "blue" },
          code: { text: "兑换码", color: "orange" },
          reward: { text: "活动奖品", color: "red" },
          wechat: { text: "微信支付", color: "green" },
        };
        const { text, color } = payTypeMap[payType] || {
          text: payType,
          color: "default",
        };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "支付状态",
      dataIndex: "pay_status",
      key: "pay_status",
      align: "center",
      render: (status: number, record) => {
        const statusMap: Record<number, { text: string; color: string }> = {
          0: { text: "未支付", color: "#ff4d4f" },
          1: { text: "已支付", color: "#52c41a" },
          2: { text: "已退款", color: "#faad14" },
        };
        const { text, color } = statusMap[status] || {
          text: "未知",
          color: "#999",
        };

        // 未支付状态
        if (
          status === 0 &&
          (record.pay_type === "alipay" || record.pay_type === "wechat")
        ) {
          return (
            <Space
              wrap
              size={[4, 4]}
              style={{ display: "flex", alignItems: "center" }}
            >
              <span style={{ color }}>{text}</span>
              <Space.Compact size="small">
                {record.pay_type === "alipay" && (
                  <Button
                    type="text"
                    icon={
                      <i
                        className="ri-bank-card-line"
                        style={{ fontSize: "16px" }}
                      />
                    }
                    onClick={() => handleGoToPay(record.pay_url || "")}
                    disabled={!record.pay_url}
                  />
                )}
                <Button
                  type="text"
                  icon={
                    <i
                      className="ri-refresh-line"
                      style={{ fontSize: "16px" }}
                    />
                  }
                  onClick={() => checkPayStatus(record.order_id)}
                  disabled={checkingStatus[record.order_id]}
                />
              </Space.Compact>
            </Space>
          );
        }

        return <span style={{ color }}>{text}</span>;
      },
    },
    {
      title: "下单时间",
      dataIndex: "created_at",
      key: "created_at",
      render: (time: string) => new Date(time).toLocaleString("zh-CN"),
    },
    {
      title: "支付时间",
      dataIndex: "pay_time",
      key: "pay_time",
      render: (time: string) => {
        return time && time !== "0001-01-01T00:00:00Z"
          ? new Date(time).toLocaleString("zh-CN")
          : "-";
      },
    },
  ];

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              <i
                className="ri-file-list-3-line"
                style={{ marginRight: "8px" }}
              ></i>
              订单记录
            </span>
            <Button
              icon={
                <i className="ri-refresh-line" style={{ marginRight: 4 }} />
              }
              onClick={refreshOrders}
              loading={loading}
            >
              刷新
            </Button>
          </div>
        }
        variant="outlined"
      >
        <Table<OrderItem>
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default MyOrders;
